import React, { useState, useEffect } from 'react';
import {
    FaPlay, FaStop, FaShieldAlt, FaExclamationTriangle, FaMicrophone,
    FaCamera, FaCommentDots, FaCheckSquare, FaCloudSun, FaTruck,
    FaMapMarkerAlt, FaPhoneAlt, FaChevronRight, FaTools, FaShoppingCart, FaBriefcase,
    FaMapMarkedAlt, FaBullhorn, FaCheckCircle, FaMicrophoneSlash, FaTimes,
    FaCalendarCheck, FaLocationArrow, FaClock, FaUtensils
} from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styles from '../../pages/Account.module.css';
import { toast } from 'react-toastify';
import pb from '../../lib/pocketbase';
import { formatLocalDate } from '../../utils/timesheetHelpers';

const TabLiveChantier = ({
    staffProjects, setSelectedProject, setShowTimesheetModal,
    setShowUploadModal, checklist, setChecklist, user,
    activeWorkSession, elapsedTime: externalElapsedTime
}) => {
    // 1. TOP-LEVEL STATE
    const [isWorkActive, setIsWorkActive] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [activities, setActivities] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [weather, setWeather] = useState(null);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [chefMessage, setChefMessage] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentMission, setCurrentMission] = useState({ name: "Chargement...", id: null, address: "" });
    const [showProjectSelector, setShowProjectSelector] = useState(false);
    const [priorityAlerts, setPriorityAlerts] = useState([]);
    const [teamSessions, setTeamSessions] = useState([]);
    const [livePhotos, setLivePhotos] = useState([]);
    const [dailyBriefing, setDailyBriefing] = useState(null);
    const [briefingConfirmed, setBriefingConfirmed] = useState(false);
    const [showBriefingRecording, setShowBriefingRecording] = useState(false);
    const [liveStep, setLiveStep] = useState(0);
    const [plannedMission, setPlannedMission] = useState(null);
    const [plannedTime, setPlannedTime] = useState('');
    const [plannedTitle, setPlannedTitle] = useState('');
    const [showQuickSwitcher, setShowQuickSwitcher] = useState(false);
    const [timeAlert, setTimeAlert] = useState(null);

    const getOrCreateSpecialProject = async (virtualProject) => {
        if (!virtualProject.type || virtualProject.type !== 'internal') return virtualProject.id;
        const targetName = virtualProject.dbName || virtualProject.name;
        try {
            const existing = await pb.collection('chantiers').getList(1, 1, { filter: `name="${targetName}"` });
            if (existing.items.length > 0) return existing.items[0].id;
            const newProject = await pb.collection('chantiers').create({
                name: targetName,
                address: virtualProject.address,
                status: 'EN_COURS',
                description: 'Projet interne généré automatiquement',
            });
            return newProject.id;
        } catch (err) {
            console.error("Error creating special project:", err);
            return null;
        }
    };

    const customOptions = [
        { id: 'atelier', name: '🏭 Atelier / Dépôt', dbName: 'Atelier', address: 'Siège', type: 'internal', color: '#64748b', sessionType: 'depot' },
        { id: 'trajet', name: '🚚 Trajet / Course', dbName: 'Trajet', address: 'En route', type: 'internal', color: '#f97316', sessionType: 'trajet' },
        { id: 'achats', name: '🛒 Achats / Fourn.', dbName: 'Achats', address: 'Magasin', type: 'internal', color: '#8b5cf6', sessionType: 'magasin' },
        { id: 'bureau', name: '💼 Bureau / Admin.', dbName: 'Bureau', address: 'Bureau', type: 'internal', color: '#0ea5e9', sessionType: 'bureau' },
        { id: 'repas', name: '🍱 Pause Repas', dbName: 'Pause Repas', address: 'Pause', type: 'internal', color: '#10b981', sessionType: 'repas' },
    ];

    // 2. STABLE INITIALIZATION
    useEffect(() => {
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        });

        const initLive = async () => {
            setLoading(true);
            try {
                const active = await checkActiveSession();
                await detectCurrentProject(active);
            } finally {
                setLoading(false);
            }
        };
        initLive();
    }, []);

    // 3. SYNC WITH PROPS
    useEffect(() => {
        if (externalElapsedTime !== undefined) setElapsedTime(externalElapsedTime);
    }, [externalElapsedTime]);

    useEffect(() => {
        if (activeWorkSession !== undefined) {
            setIsWorkActive(!!activeWorkSession);
            if (activeWorkSession) {
                setCurrentSession(activeWorkSession);
                const internalOption = customOptions.find(o => o.sessionType === activeWorkSession.type || o.id === activeWorkSession.type);
                const project = internalOption || activeWorkSession.expand?.chantier;
                if (project) {
                    setCurrentMission(project);
                    setSelectedProject(project);
                }
                setLiveStep(4);
            }
        }
    }, [activeWorkSession]);

    // 4. DATA REFRESH ON MISSION CHANGE
    useEffect(() => {
        if (currentMission && currentMission.id) {
            fetchActivities();
            fetchLivePhotos();
            fetchPriorityAlerts();
            fetchDailyBriefing();
            fetchDailyChecklist();
            if (liveStep === 4 || liveStep === 0) fetchWeather();
        }
    }, [currentMission?.id, liveStep]);

    // 5. SMARTS: TIME-BASED MONITORING
    useEffect(() => {
        const checkTimeEvents = () => {
            const now = new Date();
            const hour = now.getHours();
            const min = now.getMinutes();

            // LUNCH
            if (hour === 12 && min < 30 && isWorkActive && currentMission?.id !== 'repas') {
                setTimeAlert({ id: 'lunch', icon: <FaUtensils />, title: "Pause Déjeuner ?", text: "Il est midi ! N'oubliez pas de passer en mode 'Pause Repas' et de prendre une photo de chantier.", action: () => switchActivity(customOptions[4]) });
            }
            // SUNSET
            if (hour === 17 && min >= 30 && isWorkActive) {
                if (timeAlert?.id !== 'sunset') {
                    setTimeAlert({ id: 'sunset', icon: <FaCheckSquare />, title: "Fin de journée ?", text: "Bientôt terminé ? Pensez à remplir la checklist de propreté/sécurité avant de partir.", action: () => setTimeAlert(null) });
                }
            }
            // FRIDAY
            if (now.getDay() === 5 && hour >= 16 && !timeAlert) {
                setTimeAlert({ id: 'friday', icon: <FaCalendarCheck />, title: "Bilan Heures Semaine", text: "C'est vendredi ! Vérifiez que toutes vos heures sont bien saisies pour la paie.", action: () => { window.location.hash = "#hours"; setTimeAlert(null); } });
            }
        };
        const interval = setInterval(checkTimeEvents, 60000);
        checkTimeEvents();
        return () => clearInterval(interval);
    }, [isWorkActive, currentMission?.id, timeAlert?.id]);

    // 6. REAL-TIME SUBSCRIPTIONS
    useEffect(() => {
        const activitySub = pb.collection('site_activities').subscribe('*', function (e) {
            if (e.action === 'create') {
                const handleNew = async () => {
                    try {
                        const rec = await pb.collection('site_activities').getOne(e.record.id, { expand: 'author,chantier' });
                        setActivities(prev => [rec, ...prev].slice(0, 10));
                        if (rec.is_urgent || rec.type === 'logistique') setPriorityAlerts(prev => [rec, ...prev].slice(0, 3));
                        if (rec.type === 'photo') setLivePhotos(prev => [rec, ...prev].slice(0, 15));
                        if (rec.is_urgent) toast.error("🚨 ALERTE : " + rec.content, { autoClose: false });
                    } catch (err) { }
                };
                handleNew();
            }
        });
        const planningSub = pb.collection('planning').subscribe('*', function (e) {
            if (e.action === 'update' && e.record.worker === user.id) {
                toast.warning("📢 Votre planning a été mis à jour !", { position: "top-center", autoClose: false });
            }
        });
        const briefingSub = pb.collection('briefings').subscribe('*', function (e) {
            if (e.action === 'create' || e.action === 'update') {
                fetchDailyBriefing();
                toast.info('📢 Nouveau briefing du chef disponible !', { autoClose: 4000 });
            }
        });

        return () => {
            activitySub.then(u => u()).catch(() => { });
            planningSub.then(u => u()).catch(() => { });
            briefingSub.then(u => u()).catch(() => { });
        };
    }, [user.id]);

    const detectCurrentProject = async (activeSessionOverride = null) => {
        try {
            // 1. Check if there is an active session
            const sessionToCheck = activeSessionOverride || currentSession || activeWorkSession;
            if (sessionToCheck && sessionToCheck.expand?.chantier) {
                setCurrentMission(sessionToCheck.expand.chantier);
                return;
            }

            // 2. Fetch today's scheduled intervention for this worker
            const today = formatLocalDate(new Date());
            const records = await pb.collection('interventions').getList(1, 1, {
                filter: `assigned_staff ~ "${user.id}" && start >= "${today} 00:00:00" && start <= "${today} 23:59:59"`,
                expand: 'project_id'
            });

            if (records.items.length > 0 && records.items[0].expand?.project_id) {
                const mission = records.items[0].expand.project_id;
                const intervention = records.items[0];
                const startStr = new Date(intervention.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                const endStr = new Date(intervention.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                setPlannedMission(mission);
                setPlannedTime(`${startStr} - ${endStr}`);
                setPlannedTitle(intervention.title || 'Intervention');
                setCurrentMission(mission);
                setSelectedProject(mission); // SYNC PARENT
            } else if (staffProjects && staffProjects.length > 0) {
                setCurrentMission(staffProjects[0]);
            } else {
                setCurrentMission(customOptions[0]);
            }
        } catch (err) {
            console.error("Error detecting project:", err);
            if (staffProjects && staffProjects.length > 0) {
                setCurrentMission(staffProjects[0]);
            } else {
                setCurrentMission(customOptions[0]);
            }
        }
    };

    useEffect(() => {
        // When mission changes, refresh relevant local data
        if (currentMission && currentMission.id) {
            fetchActivities();
            fetchLivePhotos();
            fetchPriorityAlerts();
            fetchDailyBriefing();
            if (liveStep === 4) fetchWeather();
            fetchDailyChecklist();
        }
    }, [currentMission?.id, liveStep]);

    const handleProjectChange = (project) => {
        if (isWorkActive) {
            switchActivity(project);
            return;
        }
        setCurrentMission(project);
        setShowProjectSelector(false);
        setChecklist({ clean: false, secure: false, photos: false });
    };

    const fetchActivities = async () => {
        try {
            let filter = '';
            if (currentMission?.id && !currentMission.type) {
                filter = `chantier = "${currentMission.id}"`;
            } else if (currentMission?.type === 'internal') {
                const realProjectId = await getOrCreateSpecialProject(currentMission);
                if (realProjectId) filter = `chantier = "${realProjectId}"`;
            }
            if (!filter) return; // No valid mission to filter by
            const records = await pb.collection('site_activities').getList(1, 15, {
                filter,
                sort: '-created',
                expand: 'author,chantier'
            });
            setActivities(records.items);
        } catch (err) { console.error("Erreur activités:", err); }
    };

    const fetchPriorityAlerts = async () => {
        try {
            let filter = 'is_urgent = true || type = "logistique"';
            if (currentMission?.id && !currentMission.type) {
                filter = `(${filter}) && chantier = "${currentMission.id}"`;
            } else if (currentMission?.type === 'internal') {
                const realProjectId = await getOrCreateSpecialProject(currentMission);
                if (realProjectId) filter = `(${filter}) && chantier = "${realProjectId}"`;
            }
            if (!filter) return; // No valid mission to filter by
            const records = await pb.collection('site_activities').getList(1, 5, {
                filter,
                sort: '-created',
                expand: 'author,chantier',
            });
            setPriorityAlerts(records.items);
        } catch (err) { console.error("Erreur alertes prioritaires:", err); }
    };

    const fetchTeamPresence = async () => {
        try {
            const records = await pb.collection('work_sessions').getFullList({
                filter: 'is_active = true',
                expand: 'user,chantier',
                sort: '-start_time',
            });
            setTeamSessions(records);
        } catch (err) { console.error("Erreur team presence:", err); }
    };

    const fetchLivePhotos = async () => {
        try {
            const today = formatLocalDate(new Date());
            let filter = `type = "photo" && created >= "${today} 00:00:00"`;
            if (currentMission?.id && !currentMission.type) {
                filter += ` && chantier = "${currentMission.id}"`;
            } else if (currentMission?.type === 'internal') {
                const realProjectId = await getOrCreateSpecialProject(currentMission);
                if (realProjectId) filter += ` && chantier = "${realProjectId}"`;
            }
            if (!filter) return; // No valid mission to filter by
            const records = await pb.collection('site_activities').getList(1, 15, {
                filter,
                sort: '-created',
                expand: 'author,chantier',
            });
            setLivePhotos(records.items);
        } catch (err) {
            console.error("Erreur live photos:", err);
        }
    };

    const fetchDailyBriefing = async () => {
        try {
            const today = formatLocalDate(new Date());
            const records = await pb.collection('briefings').getList(1, 1, {
                filter: `created >= "${today} 00:00:00"`,
                sort: '-created',
                expand: 'author',
            });
            if (records.items.length > 0) {
                const briefing = records.items[0];
                setDailyBriefing(briefing);
                // Check if this user already confirmed
                const confirmed = (briefing.confirmed_by || []).includes(user.id);
                setBriefingConfirmed(confirmed);
            } else {
                setDailyBriefing(null);
                setBriefingConfirmed(false);
            }
        } catch (err) {
            // Collection might not exist yet — silent fail
            if (err.status !== 404) console.error("Erreur briefing:", err);
        }
    };

    const handleConfirmBriefing = async () => {
        if (!dailyBriefing || briefingConfirmed) return;
        try {
            const confirmedBy = [...(dailyBriefing.confirmed_by || []), user.id];
            await pb.collection('briefings').update(dailyBriefing.id, { confirmed_by: confirmedBy });
            setBriefingConfirmed(true);
            setDailyBriefing(prev => ({ ...prev, confirmed_by: confirmedBy }));
            toast.success('✅ Briefing lu et compris !', { autoClose: 2000 });
        } catch (err) {
            toast.error("Erreur lors de la confirmation.");
        }
    };

    const fetchWeather = async () => {
        const lat = currentMission.lat || 48.8566;
        const lon = currentMission.lon || 2.3522;
        try {
            const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,wind_speed_10m&timezone=auto`);
            const data = await response.json();
            if (data.current) {
                setWeather({
                    temp: Math.round(data.current.temperature_2m),
                    wind: Math.round(data.current.wind_speed_10m),
                    unit: data.current_units.temperature_2m,
                    windUnit: data.current_units.wind_speed_10m,
                    condition: data.current.temperature_2m > 0 ? "Ensoleillé" : "Frais", // Basic mapping
                    rainProb: 0 // Mocked for now or can fetch from hourly
                });
            }
        } catch (err) { console.error("Weather fetch error", err); }
    };

    const fetchDailyChecklist = async () => {
        if (!currentMission.id) return;
        try {
            let missionId = currentMission.id;
            if (currentMission.type === 'internal') {
                missionId = await getOrCreateSpecialProject(currentMission);
                if (!missionId) return;
            }
            const result = await pb.collection('daily_checklists').getList(1, 1, {
                filter: `user="${user.id}" && chantier="${missionId}"`,
                sort: '-created'
            });
            if (result.items.length > 0) {
                const today = formatLocalDate(new Date());
                const recordDate = result.items[0].created.split('T')[0];
                if (today === recordDate) {
                    setChecklist({
                        clean: result.items[0].is_clean,
                        secure: result.items[0].is_secured,
                        photos: result.items[0].is_reported
                    });
                }
            }
        } catch (err) { console.error("Checklist fetch error", err); }
    };

    const checkActiveSession = async () => {
        try {
            const active = await pb.collection('work_sessions').getFirstListItem(`user="${user.id}" && is_active=true`, { expand: 'chantier', sort: '-created' });
            if (active) {
                setIsWorkActive(true);
                setCurrentSession(active);
                // Handle both client sites and internal activities
                const internalOption = customOptions.find(o => o.sessionType === active.type || o.id === active.type);
                const project = internalOption || active.expand?.chantier;
                if (project) {
                    setCurrentMission(project);
                    setSelectedProject(project); // SYNC PARENT
                }
                setLiveStep(4); // Immediately go to active hub
                return active;
            }
        } catch (err) {
            // Ignore 404
        }
        return null;
    };

    useEffect(() => {
        let interval;
        if (isWorkActive && externalElapsedTime === undefined) {
            interval = setInterval(() => setElapsedTime(prev => prev + 1), 1000);
        }
        return () => clearInterval(interval);
    }, [isWorkActive, externalElapsedTime]);

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const handleToggleWork = async (forcedType = null, skipStep = false, missionOverride = null) => {
        const targetMission = missionOverride || currentMission;
        if (!targetMission?.id) { toast.warning("Aucun chantier n'est sélectionné."); return; }
        if (!isWorkActive) {
            try {
                const realProjectId = await getOrCreateSpecialProject(targetMission);
                if (!realProjectId) throw new Error("Invalid Project ID");
                // Mapping local IDs to PocketBase Select values
                const typeMapping = {
                    'atelier': 'depot',
                    'achats': 'magasin',
                    'trajet': 'trajet',
                    'bureau': 'bureau',
                    'repas': 'repas',
                    'site': 'site'
                };

                const sessionType = forcedType || (targetMission.type === 'internal'
                    ? (typeMapping[targetMission.id] || 'depot')
                    : 'site');

                const session = await pb.collection('work_sessions').create({
                    user: user.id,
                    chantier: realProjectId,
                    start_time: new Date().toISOString(),
                    is_active: true,
                    type: sessionType
                });
                setCurrentSession(session);
                setIsWorkActive(true);
                setCurrentMission(targetMission); // SYNC STATE
                setSelectedProject(targetMission); // SYNC PARENT
                if (!skipStep) setLiveStep(4); // Set to active step
                toast.info("📍 Journée démarrée !");
                await pb.collection('site_activities').create({
                    chantier: realProjectId,
                    author: user.id,
                    type: 'status',
                    content: `🏁 Déplacement démarré par ${user.name || user.username}`
                });
            } catch (err) {
                console.error(err);
                toast.error("Impossible de démarrer la session.");
            }
        } else {
            // Just open the timesheet modal. The chrono will be stopped upon successful submission in Account.js
            setShowTimesheetModal(true);
        }
    };

    const switchActivity = async (newMission) => {
        if (isWorkActive && currentSession) {
            try {
                // 1. End current session
                await pb.collection('work_sessions').update(currentSession.id, { is_active: false });

                // 2. Log closing activity
                const oldProjectId = await getOrCreateSpecialProject(currentMission);
                await pb.collection('site_activities').create({
                    chantier: oldProjectId,
                    author: user.id,
                    type: 'status',
                    content: `🔄 Passage de ${currentMission.name} à ${newMission.name}`
                });

                // 3. Start new session immediately
                const realNewProjectId = await getOrCreateSpecialProject(newMission);
                const typeMapping = {
                    'atelier': 'depot',
                    'achats': 'magasin',
                    'trajet': 'trajet',
                    'bureau': 'bureau',
                    'repas': 'repas',
                    'site': 'site'
                };
                const sessionType = newMission.forcedType || (newMission.type === 'internal'
                    ? (typeMapping[newMission.id] || 'depot')
                    : 'site');

                const newSession = await pb.collection('work_sessions').create({
                    user: user.id,
                    chantier: realNewProjectId,
                    start_time: new Date().toISOString(),
                    is_active: true,
                    type: sessionType
                });

                // 4. Update local state
                setCurrentSession(newSession);
                setCurrentMission(newMission);
                setShowQuickSwitcher(false);
                setLiveStep(4);
                toast.success(`Changé pour : ${newMission.name}`);

                // Refresh local data for the new mission
                fetchActivities();
                fetchLivePhotos();
                fetchPriorityAlerts();
            } catch (err) {
                console.error("Switch error:", err);
                toast.error("Erreur lors du changement d'activité.");
            }
        } else {
            // If not active, just set the mission for starting later
            setCurrentMission(newMission);
            setLiveStep(3);
            setShowQuickSwitcher(false);
        }
    };
    const handleQuickPhoto = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'camera';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append('attachment', file);
            formData.append('type', 'photo');
            formData.append('content', '📸 Nouvelle photo terrain');
            formData.append('author', user.id);
            const realProjectId = await getOrCreateSpecialProject(currentMission);
            if (realProjectId) formData.append('chantier', realProjectId);
            try {
                toast.promise(pb.collection('site_activities').create(formData), { pending: "Upload de la photo...", success: "Photo partagée au flux ! 🚀", error: "Erreur lors de l'envoi." });
            } catch (err) { console.error("Upload error:", err); }
        };
        input.click();
    };

    const triggerSOS = async () => {
        if (window.confirm("🚨 Voulez-vous envoyer une ALERTE URGENCE au bureau ?")) {
            try {
                const realProjectId = await getOrCreateSpecialProject(currentMission);
                await pb.collection('site_activities').create({ chantier: realProjectId, author: user.id, type: 'alerte', content: `🔴 ALERTE SOS : Incident signalé par ${user.name || user.username}`, is_urgent: true });
                window.open('tel:18');
                toast.error("Alerte envoyée. Les secours sont contactés.");
            } catch (err) { toast.error("Erreur lors de l'envoi de l'alerte."); }
        }
    };

    const handleVoiceNote = () => {
        if (!('webkitSpeechRecognition' in window)) { toast.error("Votre navigateur ne supporte pas la dictée vocale."); return; }
        const recognition = new window.webkitSpeechRecognition();
        recognition.lang = 'fr-FR';
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.onstart = () => toast.info("🎤 Parlez maintenant...");
        recognition.onresult = async (event) => {
            const transcript = event.results[0][0].transcript;
            if (transcript) {
                try {
                    const realProjectId = await getOrCreateSpecialProject(currentMission);
                    await pb.collection('site_activities').create({ chantier: realProjectId, author: user.id, type: 'memo', content: `🎤 Note Vocale : "${transcript}"` });
                    toast.success("Note vocale enregistrée !");
                } catch (err) { toast.error("Erreur d'enregistrement."); }
            }
        };
        recognition.onerror = (event) => toast.error("Erreur dictée : " + event.error);
        recognition.start();
    };

    const updateChecklist = async (key, value) => {
        const newChecklist = { ...checklist, [key]: value };
        setChecklist(newChecklist);
        if (!currentMission.id) return;
        try {
            let missionId = currentMission.id;
            if (currentMission.type === 'internal') {
                missionId = await getOrCreateSpecialProject(currentMission);
                if (!missionId) return;
            }
            const today = formatLocalDate(new Date());
            const records = await pb.collection('daily_checklists').getList(1, 1, { filter: `user="${user.id}" && chantier="${missionId}" && created>="${today} 00:00:00"` });
            const data = { user: user.id, chantier: missionId, is_clean: newChecklist.clean, is_secured: newChecklist.secure, is_reported: newChecklist.photos };
            if (records.items.length > 0) { await pb.collection('daily_checklists').update(records.items[0].id, data); }
            else { await pb.collection('daily_checklists').create(data); }
            toast.success("Checklist sauvegardée", { autoClose: 1000 });
        } catch (err) { console.error("Checklist save error", err); }
    };

    const handleSendMessageToChef = async (e) => {
        e.preventDefault();
        setIsSendingMessage(true);
        try {
            await pb.collection('messages').create({ sender: user.id, subject: `[CHANTIER] Message de ${user.username}`, body: chefMessage, is_read: false });
            const realProjectId = await getOrCreateSpecialProject(currentMission);
            if (realProjectId) { await pb.collection('site_activities').create({ chantier: realProjectId, author: user.id, type: 'info', content: `📨 Message envoyé au chef : "${chefMessage.substring(0, 20)}..."` }); }
            toast.success("Message envoyé au chef de chantier !");
            setChefMessage('');
            setShowMessageModal(false);
        } catch (err) { toast.error("Erreur lors de l'envoi."); console.error(err); }
        finally { setIsSendingMessage(false); }
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Synchronisation du Poste de Combat...</div>;

    // --- LINEAR WIZARD RENDERER ---

    // STEP 0: WELCOME - SMART ASSISTANT BRIEFING
    if (liveStep === 0) {
        return (
            <div className={styles.liveCockpit} style={{ minHeight: '60vh', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className={styles.smartAssistantCard}>
                    <div className={styles.assistantHeader} style={{ marginBottom: plannedMission ? '15px' : '30px' }}>
                        <div className={styles.assistantAvatar}>👨‍🔧</div>
                        <div>
                            <h3 className={styles.assistantTitle}>Bonjour {user.name || user.username || 'Artisan'} !</h3>
                            <p className={styles.assistantSubtitle}>Votre assistant Quinard est prêt.</p>
                        </div>
                    </div>

                    {plannedMission ? (
                        <div className={styles.missionBriefCard} style={{ marginBottom: '25px', padding: '20px', background: 'rgba(164, 198, 57, 0.08)', borderRadius: '20px', borderLeft: '5px solid var(--color-primary)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.7rem', fontWeight: 900, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Objectif du Jour</span>
                                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--color-secondary)' }}><FaClock style={{ marginRight: '5px' }} />{plannedTime || '--:--'}</span>
                            </div>
                            <h2 style={{ fontSize: '1.2rem', fontWeight: 950, margin: '5px 0', color: 'var(--color-secondary)' }}>{plannedTitle || "Intervention prévue"}</h2>
                            <p style={{ margin: '5px 0', fontSize: '1rem', fontWeight: 700, opacity: 0.8 }}>📍 {plannedMission.name}</p>
                            <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem', opacity: 0.6 }}>{plannedMission.address || 'Adresse à confirmer'}</p>
                            <div style={{ marginTop: '10px', display: 'inline-block', background: 'rgba(255,165,0,0.15)', color: '#ea580c', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                🚗 Trajet estimé : ~25 min
                            </div>
                        </div>
                    ) : (
                        <div className={styles.missionBriefCard} style={{ marginBottom: '25px', padding: '15px', background: 'var(--color-surface)', borderRadius: '15px', border: '1px solid var(--color-border)' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Où allez-vous aujourd'hui ?</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                {staffProjects?.filter(p => p.status === 'EN_COURS' || p.status === 'PLANNING').map(p => (
                                    <button key={p.id} onClick={() => { setCurrentMission(p); setLiveStep(3); }} style={{ textAlign: 'left', padding: '10px 15px', borderRadius: '10px', background: 'var(--color-background)', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: 'var(--color-text)' }}>
                                        <span style={{ fontWeight: 700 }}>{p.name}</span>
                                        <FaChevronRight style={{ opacity: 0.5, fontSize: '0.8rem' }} />
                                    </button>
                                ))}
                                {(!staffProjects || staffProjects.filter(p => p.status === 'EN_COURS' || p.status === 'PLANNING').length === 0) && (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', textAlign: 'center', padding: '10px' }}>Aucun chantier actif trouvé.</div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={styles.briefingGrid}>
                        <div className={styles.briefItem}>
                            <FaCloudSun style={{ color: '#fbbf24', fontSize: '1.5rem' }} />
                            <div>
                                <strong>Météo : {weather?.temp || '--'}°C ({weather?.condition || 'Chargement...'})</strong>
                                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.7 }}>
                                    {weather?.temp < 5 ? "❄️ Attention au gel le matin." : "☀️ Conditions idéales pour bosser."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
                        <button className={styles.startBtnLarge} onClick={() => setLiveStep(1)}>
                            CHOISIR AUTRE <FaChevronRight />
                        </button>

                        {plannedMission ? (
                            <button className={styles.startBtnLarge} onClick={() => {
                                handleToggleWork('trajet', true, plannedMission);
                                setLiveStep(4);
                            }} style={{ background: 'var(--color-secondary)' }}>
                                <FaLocationArrow /> DÉMARRER <FaChevronRight />
                            </button>
                        ) : (
                            <button className={styles.startBtnLarge} onClick={() => setLiveStep(2)} style={{ background: 'var(--color-secondary)' }}>
                                <FaPlay /> DIRECTION CHANTIER
                            </button>
                        )}
                    </div>

                    <div className={styles.stepIndicator} style={{ marginTop: '30px' }}>
                        <div className={styles.stepDotActive}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 1: ACTIVITY SELECTION
    if (liveStep === 1) {
        return (
            <div className={styles.liveCockpit} style={{ padding: '20px' }}>
                <div className={styles.wizardContainer}>
                    <div className={styles.wizardTitle}>Où allez-vous ?</div>
                    <div className={styles.activityGrid}>
                        <div onClick={() => { handleToggleWork(null, false, customOptions[0]); }} className={`${styles.activityCard}`}>
                            <FaTools /> <span>Atelier</span>
                        </div>
                        <div onClick={() => { handleToggleWork(null, false, customOptions[1]); }} className={`${styles.activityCard}`}>
                            <FaTruck /> <span>Trajet</span>
                        </div>
                        <div onClick={() => { handleToggleWork(null, false, customOptions[2]); }} className={`${styles.activityCard}`}>
                            <FaShoppingCart /> <span>Achats</span>
                        </div>
                        <div onClick={() => { handleToggleWork(null, false, customOptions[3]); }} className={`${styles.activityCard}`}>
                            <FaBriefcase /> <span>Bureau</span>
                        </div>
                        <div onClick={() => setLiveStep(2)} className={`${styles.activityCard}`} style={{ gridColumn: 'span 2', background: 'rgba(164, 198, 57, 0.1)', borderColor: 'var(--color-primary)' }}>
                            <FaMapMarkedAlt style={{ color: 'var(--color-primary)' }} />
                            <span style={{ color: 'var(--color-primary)' }}>Chantier Client</span>
                        </div>
                    </div>
                    <div className={styles.stepIndicator}>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDotActive}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 2: PROJECT SELECTION
    if (liveStep === 2) {
        return (
            <div className={styles.liveCockpit} style={{ padding: '20px' }}>
                <div className={styles.wizardContainer}>
                    <div className={styles.wizardTitle}>Quel chantier ?</div>
                    <p className={styles.wizardSubtitle}>Sélectionnez votre destination pour aujourd'hui</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {staffProjects.map(p => (
                            <button key={p.id} className={styles.projectSelectBtn} onClick={() => {
                                setCurrentMission(p);
                                setLiveStep(3);
                            }}>
                                <span style={{ fontWeight: 800 }}>{p.name}</span>
                                <FaChevronRight opacity={0.5} />
                            </button>
                        ))}
                    </div>
                    <button className={styles.focusBtnGhost} style={{ marginTop: '20px' }} onClick={() => setLiveStep(1)}>ANNULER</button>

                    <div className={styles.stepIndicator}>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDotActive}></div>
                        <div className={styles.stepDot}></div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 3: TRANSIT / ARRIVAL SIGNAL (SIGNAL D'ARRIVÉE ATTENDU)
    if (liveStep === 3) {
        const isInternal = currentMission.type === 'internal';
        return (
            <div className={styles.liveCockpit} style={{ padding: '20px' }}>
                <div className={styles.wizardContainer}>
                    <div className={styles.luckyPill} style={{ background: 'var(--color-primary)', color: 'white', padding: '10px 20px', borderRadius: '40px', fontWeight: '900', fontSize: '0.8rem', textAlign: 'center', marginBottom: '20px', border: 'none', boxShadow: '0 4px 10px rgba(164,198,57,0.2)' }}>
                        OÙ ÊTES-VOUS ACTUELLEMENT ?
                    </div>

                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <div className={`${styles.digitalTimer} ${styles.digitalTimerActive}`} style={{ fontSize: 'clamp(2.5rem, 12vw, 4.5rem)', fontFamily: 'monospace', letterSpacing: 'clamp(1px, 1vw, 4px)' }}>
                            {isWorkActive ? formatTime(elapsedTime) : '00:00:00'}
                        </div>
                        <div className={styles.timerLabel} style={{ fontWeight: 900, letterSpacing: '2px', fontSize: '0.9rem', opacity: 0.6 }}>
                            {isWorkActive ? 'LA JOURNÉE A DÉMARRÉ' : 'PRÊT À DÉMARRER'}
                        </div>
                    </div>

                    <div className={styles.activityGrid}>
                        <div
                            onClick={() => {
                                if (!isWorkActive) handleToggleWork('site', false, currentMission);
                                else switchActivity({ ...currentMission, forcedType: 'site' });
                            }}
                            className={styles.activityCard}
                            style={{ gridColumn: 'span 2', background: 'rgba(16, 185, 129, 0.1)', borderColor: '#10b981', minHeight: '100px', height: 'auto' }}
                        >
                            <div style={{ background: '#10b981', color: 'white', padding: '10px', borderRadius: '12px', marginBottom: '8px' }}><FaMapMarkerAlt /></div>
                            <span style={{ color: '#059669', fontWeight: 900 }}>JE SUIS SUR LE CHANTIER</span>
                        </div>

                        <div
                            onClick={() => {
                                if (!isWorkActive) handleToggleWork('trajet', false, currentMission);
                                else switchActivity(customOptions[1]);
                            }}
                            className={`${styles.activityCard} ${currentSession?.type === 'trajet' ? styles.active : ''}`}
                        >
                            <FaTruck /> <span>EN TRAJET</span>
                        </div>

                        <div
                            onClick={() => {
                                if (!isWorkActive) handleToggleWork('depot', false, customOptions[0]);
                                else switchActivity(customOptions[0]);
                            }}
                            className={styles.activityCard}
                        >
                            <FaTools /> <span>À L'ATELIER</span>
                        </div>

                        <div
                            onClick={() => {
                                if (!isWorkActive) handleToggleWork('magasin', false, customOptions[2]);
                                else switchActivity(customOptions[2]);
                            }}
                            className={styles.activityCard}
                        >
                            <FaShoppingCart /> <span>EN ACHATS</span>
                        </div>

                        <div
                            onClick={() => {
                                if (!isWorkActive) handleToggleWork('bureau', false, customOptions[3]);
                                else switchActivity(customOptions[3]);
                            }}
                            className={styles.activityCard}
                        >
                            <FaBriefcase /> <span>AU BUREAU</span>
                        </div>
                    </div>

                    <p style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.5, marginTop: '20px' }}>
                        Mission : {currentMission.name}
                    </p>

                    <div className={styles.stepIndicator} style={{ marginTop: '10px' }}>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDot}></div>
                        <div className={styles.stepDotActive}></div>
                    </div>
                </div>
            </div>
        );
    }

    // STEP 4: FULL OPERATIONAL COCKPIT (ACTIVE)
    return (
        <div className={styles.liveCockpit}>
            {/* SMART TIME-WAKE ALERT */}
            {timeAlert && (
                <div className={styles.timeAlertBanner}>
                    <div className={styles.timeAlertIcon}>{timeAlert.icon}</div>
                    <div style={{ flex: 1 }}>
                        <div className={styles.timeAlertTitle}>{timeAlert.title}</div>
                        <div className={styles.timeAlertText}>{timeAlert.text}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className={styles.timeAlertBtn} onClick={timeAlert.action}>C'EST FAIT</button>
                        <button className={styles.timeAlertClose} onClick={() => setTimeAlert(null)}><FaTimes /></button>
                    </div>
                </div>
            )}
            {showMessageModal && (
                <div className={styles.modalOverlay} onClick={() => setShowMessageModal(false)}>
                    <div className={styles.modalCard} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Contacter le Chef</h3>
                            <button className={styles.closeBtn} onClick={() => setShowMessageModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSendMessageToChef} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label>Message pour le chef de chantier</label>
                                <textarea className={styles.modalSelect} rows="4" placeholder="Besoin de validation sur..." value={chefMessage} onChange={e => setChefMessage(e.target.value)} required autoFocus />
                            </div>
                            <div className={styles.signatureActions}>
                                <button type="button" className={styles.exportBtn} onClick={() => setShowMessageModal(false)}>Annuler</button>
                                <button type="submit" className={styles.submitButton} disabled={isSendingMessage}>{isSendingMessage ? 'Envoi...' : 'Envoyer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {priorityAlerts.length > 0 && (
                <div className={styles.alertBar}>
                    {priorityAlerts.map(alert => (
                        <div key={alert.id} className={`${styles.alertItem} ${alert.is_urgent ? styles.alertUrgent : ''}`} style={{ background: !alert.is_urgent ? '#f59e0b' : undefined, color: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                {alert.is_urgent ? <FaExclamationTriangle size={20} /> : <FaShoppingCart size={20} />}
                                <div><span style={{ opacity: 0.9, fontSize: '0.75rem', display: 'block', textTransform: 'uppercase' }}>{alert.expand?.chantier?.name || 'CHANTIER'} - PAR {alert.expand?.author?.name || 'EQUIPE'}</span>{alert.content}</div>
                            </div>
                            <button onClick={() => setPriorityAlerts(prev => prev.filter(a => a.id !== alert.id))} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '5px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                Lu <FaTimes />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.liveHeader}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <div className={`${styles.liveStatusPill} ${styles.liveStatusActive}`}>
                        <div className={styles.pulseDot} style={{ background: '#10b981' }} />
                        EN ACTION
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {plannedMission?.id === currentMission.id && (
                            <div style={{ background: 'rgba(164,198,57,0.1)', color: 'var(--color-primary)', fontSize: '0.65rem', fontWeight: 900, padding: '4px 10px', borderRadius: '20px', border: '1px solid rgba(164,198,57,0.3)' }}>PLANIFIÉ</div>
                        )}
                        <button onClick={() => setShowQuickSwitcher(!showQuickSwitcher)} className={styles.switchActivityBtnSmall}>
                            CHANGER
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                    <div className={styles.liveProjectSelector} onClick={() => setShowQuickSwitcher(!showQuickSwitcher)} style={{ cursor: 'pointer' }}>
                        <h2 className={styles.liveProjectTitle} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {currentMission.name} <FaChevronRight size={14} style={{ opacity: 0.3, transform: showQuickSwitcher ? 'rotate(90deg)' : 'none', transition: '0.3s' }} />
                        </h2>
                        <div className={styles.liveProjectSubtitle}>
                            <FaMapMarkerAlt /> {currentMission.address || 'Localisation active'}
                        </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                        <div className={`${styles.digitalTimer} ${styles.digitalTimerActive}`} style={{ fontSize: 'clamp(1.8rem, 8vw, 3rem)', letterSpacing: 'clamp(1px, 0.5vw, 2px)' }}>
                            {formatTime(elapsedTime)}
                        </div>
                        <div className={styles.timerLabel}>TEMPS TOTAL</div>
                    </div>
                </div>

                {/* QUICK ACTION BAR (MIX PROP 2) */}
                <div className={styles.quickActionBar}>
                    <div onClick={() => switchActivity(customOptions[1])} className={`${styles.quickActionIcon} ${currentMission.id === 'trajet' ? styles.quickActionActive : ''}`} title="Passer en trajet">
                        <FaTruck />
                    </div>
                    <div onClick={() => switchActivity(customOptions[0])} className={`${styles.quickActionIcon} ${currentMission.id === 'atelier' ? styles.quickActionActive : ''}`} title="Passer à l'atelier">
                        <FaTools />
                    </div>
                    <div onClick={() => switchActivity(customOptions[2])} className={`${styles.quickActionIcon} ${currentMission.id === 'achats' ? styles.quickActionActive : ''}`} title="Passer aux achats">
                        <FaShoppingCart />
                    </div>
                    <div onClick={() => switchActivity(customOptions[4])} className={`${styles.quickActionIcon} ${currentMission.id === 'repas' ? styles.quickActionActive : ''}`} title="Pause Repas">
                        <FaUtensils />
                    </div>
                    <div onClick={() => setLiveStep(2)} className={`${styles.quickActionIcon} ${!currentMission.type ? styles.quickActionActive : ''}`} title="Changer de chantier">
                        <FaMapMarkedAlt />
                    </div>
                </div>

                {showQuickSwitcher && (
                    <div className={styles.quickSwitcherDropdown}>
                        <div className={styles.switcherLabel}>PROPOSITIONS RAPIDES</div>
                        <div className={styles.switcherGrid}>
                            {staffProjects.slice(0, 3).map(p => (
                                <button key={p.id} onClick={() => switchActivity(p)} className={styles.switcherItem}>
                                    <FaMapMarkerAlt /> {p.name}
                                </button>
                            ))}
                            <button onClick={() => setLiveStep(2)} className={styles.switcherItemMore}>
                                Voir tous les chantiers...
                            </button>
                        </div>
                    </div>
                )}

                <button
                    onClick={handleToggleWork}
                    className={`${styles.toggleWorkBtn} ${styles.btnStop}`}
                    style={{ marginTop: '20px' }}
                >
                    <FaStop style={{ fontSize: '1.4rem' }} /> TERMINER LA JOURNÉE
                </button>
            </div>

            {/* BRIEFING DU MATIN */}
            {dailyBriefing && (
                <div className={`${styles.briefingCard} ${briefingConfirmed ? styles.briefingRead : styles.briefingUnread}`}>
                    {!briefingConfirmed && (
                        <div style={{
                            position: 'absolute', top: '15px', right: '15px',
                            width: '12px', height: '12px', borderRadius: '50%',
                            background: '#f59e0b', animation: 'pulse 2s infinite'
                        }} />
                    )}

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                        <div style={{
                            width: '45px', height: '45px', borderRadius: '12px', flexShrink: 0,
                            background: briefingConfirmed ? '#22c55e' : '#f59e0b',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', transition: 'background 0.4s'
                        }}>
                            {briefingConfirmed
                                ? <FaCheckCircle color="white" size={20} />
                                : <FaBullhorn color="white" size={20} />
                            }
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <span className={styles.briefingTitle}>
                                    📢 Briefing du Matin
                                </span>
                                <span style={{
                                    fontSize: '0.65rem', color: 'var(--color-text-muted)',
                                    background: 'var(--color-light-background)', padding: '2px 8px', borderRadius: '20px'
                                }}>
                                    {dailyBriefing.expand?.author?.name || 'La Direction'} · {new Date(dailyBriefing.created).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <p className={styles.briefingMessage}>
                                {dailyBriefing.message}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                    {(dailyBriefing.confirmed_by || []).length > 0
                                        ? `✅ ${(dailyBriefing.confirmed_by || []).length} artisan(s) ont confirmé`
                                        : '⏳ En attente de confirmation de l\'équipe'
                                    }
                                </div>
                                {!briefingConfirmed ? (
                                    <button
                                        onClick={handleConfirmBriefing}
                                        style={{
                                            background: '#f59e0b', color: 'white', border: 'none',
                                            padding: '8px 20px', borderRadius: '10px', fontWeight: '800',
                                            fontSize: '0.85rem', cursor: 'pointer',
                                            boxShadow: '0 2px 8px rgba(245,158,11,0.4)',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <FaCheckCircle /> Compris, Chef !
                                    </button>
                                ) : (
                                    <span style={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <FaCheckCircle /> Confirmé par vous
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.actionGrid}>
                <div className={styles.combatBtn} onClick={handleQuickPhoto}> <FaCamera className={styles.combatIcon} style={{ color: '#3b82f6' }} /> <span className={styles.combatLabel}>Photo Rapide</span> </div>
                <div className={styles.combatBtn} onClick={handleVoiceNote}> <FaMicrophone className={styles.combatIcon} style={{ color: '#a855f7' }} /> <span className={styles.combatLabel}>Note Vocale</span> </div>
                <div className={`${styles.combatBtn} ${styles.combatBtnUrgent}`} onClick={triggerSOS}> <FaShieldAlt className={styles.combatIcon} /> <span className={styles.combatLabel}>URGENCE (18)</span> </div>
                <div className={styles.combatBtn} onClick={() => setShowMessageModal(true)}> <FaCommentDots className={styles.combatIcon} style={{ color: '#10b981' }} /> <span className={styles.combatLabel}>Message Chef</span> </div>
            </div>

            <div className={styles.liveFeeds}>
                <div className={styles.feedCard}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}> <FaChevronRight style={{ color: 'var(--color-primary)' }} /> Flux Live du Chantier </h3>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {activities.length > 0 ? activities.map((act) => (
                            <div key={act.id} className={styles.feedItem}>
                                <div className={styles.feedAvatar}> {act.expand?.author?.username?.substring(0, 2).toUpperCase() || 'HQ'} </div>
                                <div className={styles.feedContent}>
                                    <div className={styles.feedUser} style={{ color: act.type === 'alerte' ? '#e11d48' : undefined }}> {act.expand?.author?.name || act.expand?.author?.username || 'Système'} </div>
                                    <div className={styles.feedText}>{act.content}</div>
                                    {act.attachment && (<img src={pb.files.getURL(act, act.attachment)} alt="Attachement" style={{ width: '100%', borderRadius: '10px', marginTop: '10px' }} />)}
                                    <div className={styles.feedTime}>{new Date(act.created).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                            </div>
                        )) : (<div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-muted)' }}>Aucune activité récente. Soyez le premier !</div>)}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div className={`${styles.feedCard} ${styles.weatherCard}`}>
                        <h4 style={{ margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}> <FaCloudSun /> Météo Technique (Live) </h4>
                        {weather ? (<> <div style={{ fontSize: '1.2rem', fontWeight: 800 }}> {weather.temp}{weather.unit} / Vent {weather.wind}{weather.windUnit} </div> <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem', opacity: 0.8 }}> {weather.wind > 20 ? '⚠️ Vent fort : Attention aux travaux en hauteur.' : '✅ Conditions idéales pour l\'extérieur.'} </p> </>) : (<div style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>Chargement météo...</div>)}
                    </div>

                    <div className={styles.feedCard}>
                        <h4 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}> <FaBriefcase style={{ color: 'var(--color-primary)' }} /> Mur des Présences </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {teamSessions.length > 0 ? teamSessions.map(session => (
                                <div key={session.id} className={styles.presenceItem}>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.8rem' }}> {session.expand?.user?.name?.[0] || session.expand?.user?.username?.[0] || 'U'} </div>
                                        <div style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '12px', height: '12px', borderRadius: '50%', background: '#10b981', border: '2px solid white' }}></div>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: '700', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text)' }}> {session.expand?.user?.name || session.expand?.user?.username} </div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}> <span style={{ color: '#10b981' }}>●</span> {session.expand?.chantier?.name || 'Chantier'} </div>
                                    </div>
                                </div>
                            )) : (<div style={{ textAlign: 'center', padding: '10px', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}> Aucun collègue en ligne pour le moment. </div>)}
                        </div>
                    </div>

                    <div className={styles.feedCard} style={{ borderLeft: '4px solid #f59e0b' }}>
                        <h4 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text)' }}> <FaCheckSquare /> Clôture de Journée </h4>
                        <div className={styles.checklistGrid}>
                            <label className={styles.checklistLabel}> <input type="checkbox" checked={checklist.clean} onChange={(e) => updateChecklist('clean', e.target.checked)} /> Chantier Nettoyé </label>
                            <label className={styles.checklistLabel}> <input type="checkbox" checked={checklist.secure} onChange={(e) => updateChecklist('secure', e.target.checked)} /> Sécurisé </label>
                            <label className={styles.checklistLabel}> <input type="checkbox" checked={checklist.photos} onChange={(e) => updateChecklist('photos', e.target.checked)} /> Photos Envoyées </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* INTERACTIVE MINI-MAP */}
            <div className={styles.liveMapWrapper} style={{ marginTop: '20px' }}>
                <MapContainer
                    center={[currentMission.lat || 48.8566, currentMission.lon || 2.3522]}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {currentMission.id && (currentMission.lat || currentMission.lon) && (
                        <Marker position={[currentMission.lat, currentMission.lon]}>
                            <Popup>
                                <strong>🚀 Votre Mission : {currentMission.name}</strong><br />
                                {currentMission.address}
                            </Popup>
                        </Marker>
                    )}
                    {teamSessions.map(session => (
                        session.expand?.chantier && session.expand.chantier.lat && (
                            <Marker
                                key={session.id}
                                position={[session.expand.chantier.lat, session.expand.chantier.lon]}
                                icon={new L.Icon({
                                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
                                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                                    iconSize: [20, 32],
                                    iconAnchor: [12, 41],
                                    popupAnchor: [1, -34],
                                    shadowSize: [30, 30]
                                })}
                            >
                                <Popup>
                                    <strong>👷 {session.expand?.user?.name || session.expand?.user?.username}</strong><br />
                                    Sur : {session.expand.chantier.name}
                                </Popup>
                            </Marker>
                        )
                    ))}
                </MapContainer>
            </div>

            {/* STEP 3: PELLICULE DU JOUR - BOTTOM */}
            {livePhotos.length > 0 && (
                <div className={styles.photoFilmStrip}>
                    <h3 style={{ margin: '0 0 15px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text)' }}>
                        <FaCamera style={{ color: 'var(--color-primary)' }} /> Pellicule du Jour
                    </h3>
                    <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '10px', scrollbarWidth: 'none' }}>
                        {livePhotos.map(photo => (
                            <div
                                key={photo.id}
                                onClick={() => setSelectedProject(photo.expand?.chantier)}
                                style={{ flex: '0 0 140px', height: '140px', borderRadius: '15px', overflow: 'hidden', position: 'relative', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                            >
                                <img src={pb.files.getURL(photo, photo.attachment, { thumb: '200x200' })} alt="Live" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '5px 8px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', color: 'white', fontSize: '0.65rem', fontWeight: '700' }}>
                                    {photo.expand?.author?.name?.split(' ')[0] || 'Artisan'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabLiveChantier;
